/* eslint-disable max-lines */
// Hooks
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useMediaRecorder} from "../../../providers/mediaRecorder";
import {useTranslation} from "react-i18next";
import {isIPhone} from "../../../browser";
import {useActionResponseCountdown} from "../../../hooks/useActionResponseCountdown";

// Libs
import bindHotkey from "../../../libs/bindHotkey";
import {analyticsSendEvent} from "../../../libs/analytics";

// Types
import {Avatar, ErrorAudio, Challenge, ChallengeInputMode, ChallengeMode} from "../../../schema";
import {FSImageRef} from "../../../ui/fullscreen/image";
import {FSVideoRef} from "../../../ui/fullscreen/video";

// Components
import Container from "../../../ui/adaptivecontainer";
import Layout, {Header, Main} from "../../Layout";
import AvatarPlayer, {AvatarPlayerRef} from "../AvatarPlayer";
import ControlsBar from "../ControlsBar";
import ContentDisplay from "./ContentDisplay";
import ActionsHistoryPanel from "./ActionsHistoryPanel";
import {Level as ChallengeLevel, Name as ChallengeName, Timer as ChallengeTimer} from "./Challenge";
import Button from "../../../ui/button";
import ReactLink from "../../ReactLink";
import Scroll from "../../../ui/scroll";
import {ProgressBar} from "@tremor/react";
import MediaDisplay from "./MediaDisplay";

// Classes
import cn from "classnames";
import classes from "./ModernPlayer.module.css";
import scrollClasses from "../../../ui/scroll.module.css";

// UI
import {ArrowForwardIcon, FaHand} from "../../../ui/icons";

// Redux
import {PlayerReduxState} from "../../../redux/player/types";
import {
  canRecord,
  getCurrentAction,
  getCurrentScenario,
  getDialogueHistory,
  isAdaptiveResponsePlaybackActive,
  isInitAgentActionPlaybackActive,
} from "../../../redux/player/selectors";
import {changeRecognizingState, goNext, pause, unpause} from "../../../redux/player/actions";
import {useDispatch, useSelector, useStore} from "react-redux";
import {isAnyUserInputAction} from "../../../redux/player/utils";
import {floor, isEqual} from "lodash";
import {ActionWithMedia, isChoiceUserInputAction, isNonUserAction} from "../../../types";

type props = {
  challenge: Challenge;
  avatar: Avatar;
  gestures: string[];
  initialMode: "2D" | "3D";
  headless?: boolean;
  backUrl?: string;
  allowFullscreen?: boolean;
  toggleFullscreen?: () => void;
  errorsAudio?: ErrorAudio[];
};

export default function ModernPlayer({
  allowFullscreen,
  toggleFullscreen,
  errorsAudio,
  challenge,
  initialMode,
  avatar,
  gestures,
  headless,
  backUrl,
}: props) {
  const {t} = useTranslation();

  const [avatarLoaded, setAvatarLoaded] = useState<boolean>();
  const [isHistoryOpen, setHistoryOpen] = useState(false);
  const avatarPlayerRef = useRef<null | AvatarPlayerRef>();
  const store = useStore<PlayerReduxState>();
  const dispatch = useDispatch();
  const onAvatarLoaded = useCallback(() => {
    setAvatarLoaded(true);
    const state = store.getState();
    const current = getCurrentAction(state);
    const shouldResume =
      isAdaptiveResponsePlaybackActive(state) ||
      isInitAgentActionPlaybackActive(state) ||
      (current && isNonUserAction(current));
    if (state.onPause && shouldResume) {
      dispatch(unpause());
    }
  }, [store, dispatch]);
  const onAvatarUnloaded = useCallback(() => {
    setAvatarLoaded(false);
    const state = store.getState();
    const current = getCurrentAction(state);
    if (
      isAdaptiveResponsePlaybackActive(state) ||
      isInitAgentActionPlaybackActive(state) ||
      (current && isNonUserAction(current))
    ) {
      dispatch(pause());
    }
  }, [store, dispatch]);

  const {
    index,
    maxIndex,
    isMainScenario,
    action,
    canRecord,
    recognitionEngine,
    recognitionPipeline,
    currentBackground,
    showMedia,
    dialogueHistory,
    isRecognizing,
    initAgentAction,
    rpgTurnKey,
  } = useSelector(ModernPlayer.selector, isEqual);

  const actionId = action?.id;

  // Handle avatar visibility changes to fix Babylon.js canvas resize issues
  useEffect(() => {
    if (avatarPlayerRef.current) {
      // Small delay to ensure DOM is updated before resize
      setTimeout(() => {
        avatarPlayerRef.current?.resize();
      }, 100);
    }
  }, []);

  const showProgressBar = challenge.mode !== ChallengeMode.RPG_MODE;

  const fsRef = useRef<FSImageRef | FSVideoRef>(null);

  const openFullscreenMedia = useCallback(() => {
    analyticsSendEvent("playerOpenFullscreenMedia");

    setMediaIsViewed(true);
    fsRef.current?.enterFullscreen();
  }, [fsRef]);

  const [mediaIsViewed, setMediaIsViewed] = useState(false);

  useEffect(() => {
    setMediaIsViewed(false);
  }, [actionId]);

  const {recorderReady, isRecording, record, stopRecord, mediaType} = useMediaRecorder();
  const isTextInput = challenge.inputMode === ChallengeInputMode.TEXT;
  const historyToggleDisabled = !canRecord || (!isTextInput && !recorderReady);
  const isHistoryPanelVisible = isHistoryOpen && !historyToggleDisabled;
  const isUserInput = action ? isAnyUserInputAction(action) : false;
  const isRpgMode = challenge.mode === ChallengeMode.RPG_MODE;
  const stepKey = isRpgMode ? `${actionId ?? ""}-${rpgTurnKey}` : (actionId ?? "");

  const onTimerExpire = useCallback(() => {
    dispatch({type: "update", payload: {actionTimerAutoSubmit: true}});
    if (!isRecording) {
      dispatch(goNext());
    }
  }, [dispatch, isRecording]);
  const isTimerPaused = isRecognizing
    || !!(initAgentAction && !initAgentAction.played && !initAgentAction.dismissed)
    || (isRpgMode && !isRecording);

  const {remaining: actionTimerRemaining} = useActionResponseCountdown({
    limitSec: isUserInput ? (challenge.actionTimeLimit ?? 0) : 0,
    stepKey,
    onExpire: onTimerExpire,
    paused: isTimerPaused,
  });
  const timerFiredRef = useRef(false);
  useEffect(() => {
    if (isRpgMode) {
      dispatch({type: "update", payload: {actionTimerRemaining}});
    }
  }, [isRpgMode, actionTimerRemaining, dispatch]);

  useEffect(() => {
    if (actionTimerRemaining === null) {
      timerFiredRef.current = false;
      return;
    }
    if (actionTimerRemaining === 0 && !timerFiredRef.current) {
      timerFiredRef.current = true;
      if (isRecording) {
        analyticsSendEvent("playerStopRecord", {trigger: "actionTimer"});
        const hasId = stopRecord();
        if (hasId) {
          dispatch(changeRecognizingState(true));
        }
      }
    }
  }, [actionTimerRemaining, isRecording, stopRecord, dispatch]);
  useEffect(() => {
    timerFiredRef.current = false;
  }, [stepKey]);

  const complete = useCallback(() => {
    if (!isRecording) {
      return;
    }
    analyticsSendEvent("playerStopRecord", {
      trigger: "hotkey",
    });
    const hasId = stopRecord();
    if (hasId) {
      dispatch(changeRecognizingState(true));
    }
  }, [isRecording, dispatch, stopRecord]);

  const startRecord = useCallback(() => {
    if (isRecording) {
      return;
    }
    analyticsSendEvent("playerStartRecord", {
      trigger: "hotkey",
      mediaType,
    });

    record(recognitionEngine!, recognitionPipeline);
  }, [isRecording, mediaType, recognitionEngine, recognitionPipeline, record]);

  useEffect(() => {
    if (challenge.inputMode === ChallengeInputMode.VOICE && canRecord && recorderReady) {
      return bindHotkey(32, startRecord, complete);
    }

    return () => {};
  }, [canRecord, recorderReady, challenge, startRecord, complete]);

  const [isControlsBarUpButtonShow, setControlsBarUpButtonShow] = useState(false);
  const [isControlsBarUpper, setControlsBarUpper] = useState(false);

  const toggleHistory = useCallback(() => setHistoryOpen((v) => !v), []);

  useEffect(() => {
    if (challenge.id === "3076" || challenge.id === "3078" || challenge.id === "3079") {
      setControlsBarUpButtonShow(true);
    }
  }, [challenge]);

  useEffect(() => {
    if (avatar.id === "none") {
      setAvatarLoaded(true);
    }
  }, [avatar.id]);

  const upControlsBar = useCallback(() => {
    setControlsBarUpButtonShow(false);
    setControlsBarUpper(true);
  }, []);

  const controlsUpButton = useMemo(
    () => (
      <Button
        className={cn(classes.backBtn, classes.upControlsBarButton, classes.hideOnDesktop)}
        onClick={upControlsBar}
        color="danger"
      >
        {t("player.upControlsBar")}
      </Button>
    ),
    [t, upControlsBar],
  );

  const extra = useMemo(
    () => (
      <Button as={ReactLink} className={cn(classes.backBtn, classes.hideOnMobile)} href={backUrl} color="danger">
        {t("player.endChallenge")}
      </Button>
    ),
    [backUrl, t],
  );

  const [timeLimit, setTimeLimit] = useState<number | undefined>(
    challenge.timeLimit && challenge.timeLimit > 0 ? challenge.timeLimit * 60 : undefined,
  );

  const updateTimeLimit = useCallback(() => {
    if (timeLimit && timeLimit > 0) {
      setTimeLimit(timeLimit - 1);
    }
  }, [timeLimit, setTimeLimit]);

  useEffect(() => {
    setTimeout(() => {
      updateTimeLimit();
    }, 1000);
  }, [updateTimeLimit]);

  const contentRef = useRef(null);
  const [hasVerticalScroll, setHasVerticalScroll] = useState(false);
  const [isScrollBottomHalf, setIsScrollBottomHalf] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      setInterval(() => {
        if (contentRef.current) {
          const {scrollHeight, clientHeight, scrollTop} = contentRef.current;
          setHasVerticalScroll(scrollHeight > clientHeight);
          setIsScrollBottomHalf(scrollTop > (scrollHeight - clientHeight) / 2);
        }
      }, 400);
    };

    checkScroll();
  }, []);

  const [showScrollGesture, setShowScrollGesture] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowScrollGesture(true), 1500);
    return () => {
      clearTimeout(timer);
      setShowScrollGesture(false);
    };
  }, [action]);

  const scrollHelp = useMemo(() => {
    const renderScrollHelpIcon = (helpText?: string) => (
      <div className={classes.scrollHelp}>
        {helpText}
        <ArrowForwardIcon className={isScrollBottomHalf ? classes.scrollHelpTop : classes.scrollHelpBot} />
      </div>
    );
    let helpText = undefined;
    if (isChoiceUserInputAction(action))
      helpText = t("player.scroll.choiceInput.helpText", {count: action.branches.length});
    return <>{renderScrollHelpIcon(helpText)}</>;
  }, [action, isScrollBottomHalf, t]);

  const contentDisplay = useMemo(() => {
    const contentClass = avatar.id !== "none" ? classes.content : cn(classes.content, classes.noAvatar);
    const progressBarValue = floor((index / (maxIndex || 1)) * 100);

    const renderContentDisplay = () => (
      <ContentDisplay
        challenge={challenge}
        avatar={avatar}
        avatarLoaded={avatarLoaded}
        openFullscreenMedia={openFullscreenMedia}
        ref={fsRef}
        errorsAudio={errorsAudio}
        isHistoryOpen={isHistoryPanelVisible}
      />
    );

    const renderScenarioHelp = () =>
      !isMainScenario && <span className="text-white mb-[4px] text-xs">{t("player.additionalScenario")}</span>;

    if (isIPhone()) {
      return (
        <>
          <div ref={contentRef} className={cn(contentClass, "relative")}>
            <Scroll>{renderContentDisplay()}</Scroll>
            {hasVerticalScroll && showScrollGesture && (
              <div className={classes.scrollGesture}>
                <FaHand size={30} />
              </div>
            )}
          </div>
          {hasVerticalScroll && scrollHelp}
          {renderScenarioHelp()}
          {showProgressBar && (
            <div className="flex flex-row justify-between gap-3 w-full">
              <ProgressBar className={"w-full"} color="green" value={progressBarValue} />
              {actionTimerRemaining !== null && (
                <ChallengeTimer timeLimit={actionTimerRemaining ?? undefined} />
              )}
            </div>
          )}
        </>
      );
    }
    return (
      <>
        <div ref={contentRef} className={cn(scrollClasses.root, contentClass, classes.contentScroll)}>
          {renderContentDisplay()}
        </div>
        {hasVerticalScroll && scrollHelp}
        {renderScenarioHelp()}
        {showProgressBar && (
          <div
            className="flex flex-row items-center justify-center gap-3 w-full"
          >
            <ProgressBar className="w-2/3" color="green" value={progressBarValue} />
            {actionTimerRemaining !== null && (
              <ChallengeTimer timeLimit={actionTimerRemaining ?? undefined} />
            )}
          </div>
        )}
      </>
    );
  }, [
    avatar,
    avatarLoaded,
    errorsAudio,
    hasVerticalScroll,
    index,
    isMainScenario,
    challenge,
    maxIndex,
    openFullscreenMedia,
    scrollHelp,
    showScrollGesture,
    actionTimerRemaining,
    showProgressBar,
    isHistoryPanelVisible,
    t,
  ]);

  const challengeInfoClassName = currentBackground ? classes.infoAlt : undefined;

  return (
    <Layout
      className={cn(classes.layoutRoot, {
        [classes.layoutRootWithBgAlt]: currentBackground,
        [classes.layoutRootWithBg]: !!challenge?.background && !currentBackground,
      })}
      style={
        currentBackground || challenge.background
          ? {
              backgroundImage: `url(${currentBackground ?? challenge.background})`,
            }
          : undefined
      }
      player
    >
      <div className={classes.blurredBackgroundWrapper}>
        <img
          className={classes.blurredBackground}
          alt=""
          src={currentBackground ?? (challenge?.background ? challenge.background : undefined)}
        />
      </div>
      {!headless && (
        <Header
          className={classes.header}
          type={challenge?.background ? "shaded" : "transparent"}
          hideLinks
          adaptive
          extra={extra}
        />
      )}
      <Main className={classes.layout}>
        <Container className={classes.root}>
          {((avatar.id !== "none" && (!showMedia))) && (

            <div className={classes.playerContainer}>
              <div className={classes.player}>
                <AvatarPlayer
                  className={classes.avatar}
                  ref={(x) => (avatarPlayerRef.current = x)}
                  initialMode={initialMode}
                  avatar={avatar}
                  gestures={gestures}
                  hideLoaderOnMobile
                  hideSwitchOnMobile
                  onLoaded={onAvatarLoaded}
                  onUnloaded={onAvatarUnloaded}
                />
              </div>
            </div>
          )}
          {(showMedia) && (
            <MediaDisplay openFullscreenMedia={openFullscreenMedia} ref={fsRef} />
          )}
          <div className={classes.contentContainer}>
            <div className={classes.challengeContainer}>
              {isControlsBarUpButtonShow && controlsUpButton}
              <ChallengeName className={challengeInfoClassName} challengeName={challenge.name} />
              <ChallengeLevel className={challengeInfoClassName} />
              <ChallengeTimer className={challengeInfoClassName} timeLimit={timeLimit} />
            </div>
            {isHistoryPanelVisible && dialogueHistory.length > 0 && (
              <ActionsHistoryPanel
                dialogueHistory={dialogueHistory}
                avatar={avatar}
                openFullscreenMedia={openFullscreenMedia}
              />
            )}
            {contentDisplay}
            <ControlsBar
              allowFullscreen={allowFullscreen}
              toggleFullscreen={toggleFullscreen}
              className={cn(classes.controlsBar, isControlsBarUpper ? classes.controlsBarUpper : undefined)}
              openFullscreenMedia={openFullscreenMedia}
              mediaIsViewed={mediaIsViewed}
              avatarLoaded={avatarLoaded}
              switchAvatarMode={avatarPlayerRef.current?.switchMode}
              avatarMode={avatarPlayerRef.current?.mode}
              backUrl={backUrl}
              recordingClassName={classes.recording}
              showHistoryToggle={dialogueHistory.length > 0}
              isHistoryOpen={isHistoryOpen}
              onToggleHistory={toggleHistory}
            />
          </div>
        </Container>
      </Main>
    </Layout>
  );
}

ModernPlayer.selector = (state: PlayerReduxState) => ({
  index: state.index,
  maxIndex: (getCurrentScenario(state).actions).length,
  isMainScenario: getCurrentScenario(state).isMain,
  action: getCurrentAction(state),
  canRecord: canRecord(state),
  recognitionEngine: state.recognitionEngine,
  recognitionPipeline: state.recognitionPipeline,
  currentBackground: state.currentBackground,
  showMedia:
    getCurrentAction(state)?.__typename === "SystemAction" || getCurrentAction(state)?.__typename === "AvatarAction"
      ? !!(getCurrentAction(state) as ActionWithMedia)?.media &&
        (getCurrentAction(state) as ActionWithMedia)?.replaceCharacterWithMedia
      : false,
  dialogueHistory: getDialogueHistory(state),
  isRecognizing: state.isRecognizing,
  initAgentAction: state.initAgentAction,
  rpgTurnKey: state.rpgTurnKey,
});
